/*
 *  -----------------------------------------------------------------------
 *    <copyright file="layout-context.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useReducer, useMemo, Dispatch, PropsWithChildren } from 'react';
import { Location } from 'react-router';
import { ILocalState } from '@contexts/local-state-context';
import { ITab } from '@config/routes/interfaces';

interface ILayoutContext {
    state: ILayoutContextState;
    dispatch: Dispatch<ILayoutAction>;
}

export interface ILayoutContextState {
    tabs: ITab[];
    breadcrumbs: string[];
    pageBreadcrumbs: (string | ((state: ILocalState, location: Location<any>) => string))[];
}

type ILayoutActionType = 'SET_TABS' | 'SET_BREADCRUMBS' | 'SET_PAGE_BREADCRUMBS';

interface ILayoutAction extends Partial<ILayoutContextState> {
    type: ILayoutActionType;
}

const defaultSettings: ILayoutContextState = {
    tabs: [],
    breadcrumbs: [],
    pageBreadcrumbs: [],
};

export const LayoutContext = React.createContext<ILayoutContext>({
    state: defaultSettings,
    dispatch: () => {},
});

const reducer = (state: ILayoutContextState, action: ILayoutAction) => {
    switch (action.type) {
        case 'SET_TABS':
            return {
                ...state,
                tabs: action.tabs ?? [],
            };

        case 'SET_BREADCRUMBS':
            return {
                ...state,
                breadcrumbs: action.breadcrumbs ?? [],
            };

        case 'SET_PAGE_BREADCRUMBS':
            return {
                ...state,
                pageBreadcrumbs: action.pageBreadcrumbs ?? [],
            };

        default:
            throw new Error();
    }
};

const LayoutContextProvider = ({ children }: PropsWithChildren) => {
    const [state, dispatch] = useReducer(reducer, defaultSettings);

    return (
        <LayoutContext.Provider value={useMemo(() => ({ state, dispatch }), [state, dispatch])}>
            {children}
        </LayoutContext.Provider>
    );
};

export default LayoutContextProvider;
